import { NumberFieldDefinition } from "@src/core/engine/helpers/fieldDefinitions/getNumberDefinition";
import { processNumericValue } from "@src/core/engine/helpers/processNumericValue";
import { utilsRepository } from "@src/core/engine/utils/utilsRepository";
import { getFormatMaxDecimals } from "@src/shared/helpers/number";
import { useUnitConversion } from "@src/shared/hooks/useUnitConversion";
import { isNumber } from "radashi";
import { useCallback, useMemo } from "react";

type Value = string | null | undefined;

export function useConvertedFieldValue(definition: NumberFieldDefinition) {
  const { convertToUserUnit } = useUnitConversion();

  const convertedMin = useMemo(
    () =>
      processNumericValue(definition.min, definition, {
        conversionFn: convertToUserUnit,
      }),
    [definition, convertToUserUnit],
  );
  const convertedMax = useMemo(
    () =>
      processNumericValue(definition.max, definition, {
        conversionFn: convertToUserUnit,
      }),
    [definition, convertToUserUnit],
  );

  const convertFieldValue = useCallback(
    (fieldValue: Value) => {
      if (!fieldValue) return "";

      const parsedFieldValue = parseFloat(fieldValue);
      if (!isNumber(parsedFieldValue)) return "";

      const convertedValue = processNumericValue(parsedFieldValue, definition, {
        conversionFn: convertToUserUnit,
        range: { max: convertedMax, min: convertedMin },
      });
      const rounded = utilsRepository.roundNumber(
        convertedValue,
        getFormatMaxDecimals(convertedValue, definition.maxDecimal),
      );
      return rounded.toString();
    },
    [convertedMax, convertedMin, definition, convertToUserUnit],
  );

  return { convertedMax, convertedMin, convertFieldValue };
}
